import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type ViewState =
  | { view: 'agency' }
  | { view: 'package'; packageId: number };

interface NavigationContextType {
  currentView: ViewState;
  navigateToPackage: (id: number) => void;
  navigateToAgency: () => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ViewState>({ view: 'agency' });

  const navigateToPackage = useCallback((id: number) => {
    const newState: ViewState = { view: 'package', packageId: id };
    window.history.pushState(newState, '', window.location.pathname);
    setState(newState);
    window.scrollTo({ top: 0 });
  }, []);

  const navigateToAgency = useCallback(() => {
    const newState: ViewState = { view: 'agency' };
    window.history.pushState(newState, '', window.location.pathname);
    setState(newState);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    window.history.replaceState({ view: 'agency' }, '', window.location.pathname);

    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.view === 'package' && event.state?.packageId) {
        setState({ view: 'package', packageId: event.state.packageId });
      } else {
        setState({ view: 'agency' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <NavigationContext.Provider value={{ currentView: state, navigateToPackage, navigateToAgency }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigation must be used within NavigationProvider');
  return context;
}
